import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabase';
import { captureReferralCode, applyStoredReferral } from '@/lib/referral';
import { toast } from 'sonner';

/**
 * What useAuth() returns. Typed so the type check can see the fields: an
 * untyped `createContext({})` made every `const { user } = useAuth()` an error
 * ("Property 'user' does not exist on type '{}'"), which buried the real ones.
 *
 * @typedef {object} AuthValue
 * @property {any} session   `{ access_token, user }`, or null when signed out
 * @property {any} user      the signed-in user, or null
 * @property {any} profile   the account's profile row; null until it loads
 * @property {(next: any) => void} setProfile
 * @property {() => Promise<any>} refreshProfile
 * @property {() => Promise<void>} signOut
 * @property {boolean} loading
 * @property {boolean} isNewUser
 * @property {() => void} clearNewUser
 */

const AuthContext = createContext(/** @type {AuthValue} */ ({}));

/** @returns {AuthValue} */
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);
  // The signed-in account's profile row (name, photo, plan, credits), shared so
  // a change saved in Settings shows up in the navbar and the dashboard at
  // once instead of after the next sign-in. Components that fetched their own
  // copy still may; this is the one to refresh after a change.
  const [profile, setProfile] = useState(null);

  // Track which user IDs have already had their referral applied this session
  // so we don't fire the API call on every token refresh.
  const appliedRef = useRef(new Set());

  // Capture ?ref= query param on first load (before any auth state fires)
  useEffect(() => {
    captureReferralCode();
  }, []);

  useEffect(() => {
    // Initialise from existing session
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Identify user in Sentry on sign-in, clear on sign-out
      if (event === 'SIGNED_IN' && session?.user) {
        Sentry.setUser({ id: session.user.id, email: session.user.email });
      } else if (event === 'SIGNED_OUT') {
        Sentry.setUser(null);
      }

      // Detect brand-new signups (created within the last 90 seconds)
      if (event === 'SIGNED_IN' && session?.user) {
        const createdAt = new Date(session.user.created_at);
        const now = new Date();
        const secondsSinceCreation = (now - createdAt) / 1000;
        if (secondsSinceCreation < 90) {
          setIsNewUser(true);
          setTimeout(() => {
            toast.success('Registration successful! Welcome to BuildersLab 🎉', {
              duration: 4000,
            });
          }, 600);

          // Fire-and-forget: send welcome email via the notifications endpoint
          const apiUrl = import.meta.env.VITE_API_URL ?? '';
          fetch(`${apiUrl}/api/notifications/welcome`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
            },
          }).catch(() => {});
        }
      }

      // Auto-apply a stored referral code on the SIGNED_IN event only.
      // We also guard with appliedRef so token refreshes don't repeat the call.
      if (
        (event === 'SIGNED_IN' || event === 'USER_UPDATED') &&
        session?.access_token &&
        session.user?.id &&
        !appliedRef.current.has(session.user.id)
      ) {
        appliedRef.current.add(session.user.id);
        applyStoredReferral(session.access_token);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  /**
   * Re-read the account from the server: the session's user (name, photo) and
   * the profile row. Call it after anything that changes either.
   */
  const refreshProfile = useCallback(async () => {
    const { data } = await supabase.auth.refreshSession();
    if (data?.profile) setProfile(data.profile);
    return data?.profile || null;
  }, []);

  // Load the profile once per signed-in account, and forget it on sign-out.
  const userId = user?.id;
  useEffect(() => {
    if (!userId) { setProfile(null); return undefined; }
    let cancelled = false;
    supabase.from('profiles').select('*').eq('id', userId).single()
      .then(({ data }) => { if (!cancelled && data) setProfile(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userId]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const clearNewUser = () => setIsNewUser(false);

  const value = {
    session,
    user,
    profile,
    setProfile,
    refreshProfile,
    signOut,
    loading,
    isNewUser,
    clearNewUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
